import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Plug, Unplug, Loader2, Eye, EyeOff, Activity, Shield, Sparkles, MessageSquare, Mic, Image as ImageIcon, Video, Languages, Music2, Search, Bell, Network, Stethoscope, KeyRound, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import RoutingPanel from "@/components/RoutingPanel";
import DiagnosticsPanel from "@/components/DiagnosticsPanel";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const CAP_ICON = {
  chat: MessageSquare, voice: Mic, image: ImageIcon, video: Video,
  translation: Languages, music: Music2, search: Search,
};
const MODE_META = {
  byok:        { label: "Your Key",         color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  managed:     { label: "Managed",          color: "text-brand-amber", bg: "bg-brand-amber/10", border: "border-brand-amber/30" },
  coming_soon: { label: "Coming Soon",      color: "text-ink-muted",   bg: "bg-bg-elevated",    border: "border-line" },
};

const ConnectForm = ({ provider, onDone }) => {
  const [keyVal, setKeyVal] = useState("");
  const [labelVal, setLabelVal] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!keyVal.trim()) { toast.error("API key required"); return; }
    setSaving(true);
    try {
      await api.post(`/providers/${provider.key}/connect`, { api_key: keyVal.trim(), label: labelVal.trim() });
      toast.success(`Connected ${provider.label}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Connect failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-2 mt-2">
      {provider.key_help_url && (
        <a
          href={provider.key_help_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`get-key-form-${provider.key}`}
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-brand-amber hover:underline"
        >
          <KeyRound className="h-3 w-3" />
          Don&apos;t have a key? Get one from {provider.vendor}
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
      <div className="relative">
        <input
          type={show ? "text" : "password"} value={keyVal}
          onChange={(e) => setKeyVal(e.target.value)}
          data-testid={`key-input-${provider.key}`}
          placeholder={`Paste your ${provider.label} API key`}
          className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 pr-9 text-xs font-mono focus:border-brand-amber focus:outline-none"
        />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <input
        value={labelVal} onChange={(e) => setLabelVal(e.target.value)}
        data-testid={`label-input-${provider.key}`}
        placeholder="Label (optional)"
        className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 text-xs focus:border-brand-amber focus:outline-none"
      />
      <div className="flex gap-2">
        <button onClick={submit} disabled={saving} data-testid={`save-${provider.key}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-amber text-bg font-bold text-xs disabled:opacity-50">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />} Save
        </button>
        <button onClick={onDone} className="px-3 py-1.5 rounded-md border border-line text-xs">Cancel</button>
      </div>
    </div>
  );
};

const PROVIDER_DIAG_LABELS = {
  credentials_loaded: "Credentials saved",
  key_present: "API key present",
  host_reachable: "Provider host reachable",
  auth_probe: "Auth + endpoint probe",
  response_shape: "Provider speaks JSON",
};

const ProviderCard = ({ provider, isSuperAdmin, onChange }) => {
  const m = MODE_META[provider.mode];
  const [editing, setEditing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [healthy, setHealthy] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/providers/${provider.key}/test`);
      const ok = !!r.data.ok;
      const verified = r.data.verified;
      const status = r.data.status;
      setHealthy(ok);
      if (ok) {
        toast.success(`${provider.label}: ${r.data.message}`);
      } else if (status === 401 || status === 403) {
        toast.error(`${provider.label}: ${r.data.message} — rotate the key`, { duration: 10000 });
      } else {
        toast.warning(`${provider.label}: ${r.data.message}`, { duration: 9000 });
      }
      // Refresh parent list so the badge updates on screen
      if (verified !== undefined) onChange?.();
    } catch (e) {
      setHealthy(false);
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const diagnose = async () => {
    setDiagBusy(true);
    setDiagResult(null);
    try {
      const r = await api.post(`/providers/${provider.key}/diagnose`);
      setDiagResult(r.data);
      if (r.data?.ok) toast.success(`${provider.label}: all checks passed.`);
      else toast.warning(r.data?.summary || "Diagnostics found an issue.", { duration: 9000 });
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostics failed.");
    } finally {
      setDiagBusy(false);
    }
  };

  const disconnect = async () => {
    if (!await appConfirm(`Disconnect ${provider.label}?`)) return;
    try { await api.post(`/providers/${provider.key}/disconnect`); toast.success("Disconnected"); onChange(); }
    catch { toast.error("Failed to disconnect"); }
  };

  const toggleAdmin = async () => {
    try {
      await api.patch(`/providers/${provider.key}/admin`, { enabled: !provider.admin_enabled });
      toast.success(provider.admin_enabled ? "Provider disabled" : "Provider enabled");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid={`provider-${provider.key}`}
      className={`rounded-xl border p-4 ${provider.connected ? "border-emerald-500/30 bg-emerald-500/5" : "border-line bg-bg-surface"} ${!provider.admin_enabled ? "opacity-40" : ""}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="type-list-title text-ink-primary">{provider.label}</div>
          <div className="type-list-subtitle text-ink-muted">{provider.vendor}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${m.color} ${m.bg} ${m.border} border`}>{m.label}</span>
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        {provider.capabilities.map((c) => {
          const Icon = CAP_ICON[c] || Sparkles;
          return (
            <span key={c} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-bg-elevated text-ink-secondary" data-testid={`cap-${provider.key}-${c}`}>
              <Icon className="h-3 w-3" /> {c}
            </span>
          );
        })}
      </div>

      <div className="text-[11px] text-ink-muted mb-2 line-clamp-2">{provider.notes}</div>

      {provider.mode === "managed" && (
        <div className="text-[11px] text-brand-amber/80 mb-2">
          Powered by BroadcastFlow AI · {provider.credit_cost} credits / generation
        </div>
      )}
      {provider.mode === "byok" && provider.connected && (
        <div className="text-[11px] mb-2 flex items-center gap-2 flex-wrap">
          <span className="text-emerald-300">● Connected{provider.account_label ? ` · ${provider.account_label}` : ""} · Using your account (no BFAI credits)</span>
          {provider.verified === true && (
            <span data-testid={`verified-${provider.key}`} className="inline-block rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">Verified</span>
          )}
          {provider.verified === false && (
            <span data-testid={`unverified-${provider.key}`} className="inline-block rounded-full border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" title={`Last probe: HTTP ${provider.last_probe_status || 0}`}>Unverified</span>
          )}
          {provider.verified === null && provider.last_tested_at && (
            <span className="inline-block rounded-full border border-line bg-bg-elevated text-ink-muted px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">No probe</span>
          )}
        </div>
      )}
      {healthy === true && <div className="text-[11px] text-emerald-300 mb-2">Test ✓ healthy</div>}
      {healthy === false && <div className="text-[11px] text-brand-onair mb-2">Test ✗ failed</div>}

      {/* Actions */}
      {provider.mode === "coming_soon" ? (
        <button disabled data-testid={`coming-${provider.key}`} className="text-xs px-3 py-1.5 rounded-md border border-line text-ink-muted cursor-not-allowed">
          Coming Soon
        </button>
      ) : provider.mode === "managed" ? (
        <div className="flex flex-wrap gap-2">
          <button onClick={test} disabled={testing} data-testid={`test-${provider.key}`}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-brand-blue/30 bg-brand-blue/10 text-brand-blue disabled:opacity-50">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />} Test
          </button>
          {isSuperAdmin && (
            <button onClick={toggleAdmin} data-testid={`admin-${provider.key}`}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-line">
              <Shield className="h-3.5 w-3.5" /> {provider.admin_enabled ? "Disable" : "Enable"}
            </button>
          )}
        </div>
      ) : editing ? (
        <ConnectForm provider={provider} onDone={() => { setEditing(false); onChange(); }} />
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {provider.connected ? (
              <>
                <button onClick={test} disabled={testing} data-testid={`test-${provider.key}`}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-brand-blue/30 bg-brand-blue/10 text-brand-blue disabled:opacity-50">
                  {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />} Test
                </button>
                <button onClick={diagnose} disabled={diagBusy} data-testid={`diagnose-${provider.key}`}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 disabled:opacity-50">
                  {diagBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stethoscope className="h-3.5 w-3.5" />} Diagnose
                </button>
                <button onClick={() => setEditing(true)} data-testid={`rotate-${provider.key}`} className="text-xs px-3 py-1.5 rounded-md border border-line">Rotate key</button>
                <button onClick={disconnect} data-testid={`disconnect-${provider.key}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-line hover:text-brand-onair">
                  <Unplug className="h-3.5 w-3.5" /> Disconnect
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} data-testid={`connect-${provider.key}`}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-brand-amber/10 text-brand-amber border border-brand-amber/30">
                  <Plug className="h-3.5 w-3.5" /> Connect
                </button>
                {provider.key_help_url && (
                  <a
                    href={provider.key_help_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`get-key-${provider.key}`}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-line text-ink-secondary hover:border-brand-amber/40 hover:text-brand-amber"
                    title={`Sign up / grab your API key at ${provider.vendor}`}
                  >
                    <KeyRound className="h-3.5 w-3.5" /> Get API key
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </>
            )}
            {isSuperAdmin && (
              <button onClick={toggleAdmin} data-testid={`admin-${provider.key}`}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-line">
                <Shield className="h-3.5 w-3.5" /> {provider.admin_enabled ? "Disable" : "Enable"}
              </button>
            )}
          </div>
          {diagResult && (
            <DiagnosticsPanel
              result={diagResult}
              labels={PROVIDER_DIAG_LABELS}
              onClose={() => setDiagResult(null)}
              testid={`diagnostics-panel-${provider.key}`}
            />
          )}
        </>
      )}
    </div>
  );
};

export default function AIProviderCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([api.get("/providers"), api.get("/auth/me").catch(() => ({ data: null }))]);
      setData(r.data); setUser(u.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load providers");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-10 text-center font-mono text-xs text-ink-muted">Loading providers…</div>;
  if (!data) return null;

  const isSuperAdmin = ["super_admin", "sysadmin", "owner"].includes((user?.role || ""));
  const filtered = filter === "all" ? data.providers : data.providers.filter((p) => p.mode === filter);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="ai-provider-center">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Dashboard
      </Link>

      <PageHeader
        eyebrow="// AI PROVIDER CENTER"
        title="One hub. Every AI provider."
        description="Connect your own API keys for full control (BYOK), or let BroadcastFlow handle everything via Managed providers using your credits. New providers slot in without app changes."
      />

      <CenterTabs data={data} isSuperAdmin={isSuperAdmin} filter={filter} setFilter={setFilter} filtered={filtered} onChange={load} />
    </div>
  );
}

function CenterTabs({ data, isSuperAdmin, filter, setFilter, filtered, onChange }) {
  const [tab, setTab] = useState("providers");
  return (
    <>
      <div className="flex items-center gap-1 mb-5 border-b border-line" data-testid="center-tabs">
        <button
          onClick={() => setTab("providers")}
          data-testid="tab-providers"
          className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 transition ${tab === "providers" ? "border-brand-amber text-brand-amber" : "border-transparent text-ink-muted hover:text-ink-primary"}`}
        >
          <Sparkles className="inline h-3.5 w-3.5 mr-1.5" /> Providers ({data.total})
        </button>
        <button
          onClick={() => setTab("routing")}
          data-testid="tab-routing"
          className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 transition ${tab === "routing" ? "border-brand-amber text-brand-amber" : "border-transparent text-ink-muted hover:text-ink-primary"}`}
        >
          <Network className="inline h-3.5 w-3.5 mr-1.5" /> Routing &amp; Failover
        </button>
      </div>

      {tab === "providers" && (
        <>
          {/* Alerts shortcut */}
          <div className="mb-6 flex items-center justify-between rounded-xl border border-line bg-bg-surface p-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="rounded-md p-2 bg-brand-amber/10 text-brand-amber shrink-0">
                <Bell className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="type-list-title text-ink-primary">Email alerts for dead providers</div>
                <div className="type-list-subtitle text-ink-muted">Get pinged when a BYOK provider stops responding — 24h cooldown, recovery emails on the bounce-back.</div>
              </div>
            </div>
            <Link
              to="/dashboard/providers/alerts"
              data-testid="open-alerts-link"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-brand-amber/30 bg-brand-amber/10 text-brand-amber font-bold shrink-0 ml-3 hover:bg-brand-amber/20 transition"
            >
              Open Alerts →
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="kpi-total">
              <div className="type-card-meta-label uppercase tracking-wider text-ink-muted">Providers</div>
              <div className="font-display text-3xl font-bold mt-1">{data.total}</div>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="kpi-connected">
              <div className="type-card-meta-label uppercase tracking-wider text-emerald-300">Connected</div>
              <div className="font-display text-3xl font-bold mt-1 text-emerald-200">{data.connected}</div>
            </div>
            <div className="rounded-xl border border-brand-amber/30 bg-brand-amber/5 p-4" data-testid="kpi-managed">
              <div className="type-card-meta-label uppercase tracking-wider text-brand-amber">Managed</div>
              <div className="font-display text-3xl font-bold mt-1 text-brand-amber">{data.counts.managed}</div>
            </div>
            <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="kpi-coming-soon">
              <div className="type-card-meta-label uppercase tracking-wider text-ink-muted">Coming Soon</div>
              <div className="font-display text-3xl font-bold mt-1">{data.counts.coming_soon}</div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="filter-bar">
            {[
              ["all", `All (${data.total})`],
              ["byok", `Your Key (${data.counts.byok})`],
              ["managed", `Managed (${data.counts.managed})`],
              ["coming_soon", `Coming Soon (${data.counts.coming_soon})`],
            ].map(([k, lbl]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
                className={`text-xs px-3 py-1.5 rounded-md border transition ${filter === k ? "border-brand-amber bg-brand-amber/10 text-brand-amber" : "border-line bg-bg-surface text-ink-secondary hover:border-brand-amber/40"}`}>{lbl}</button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((p) => <ProviderCard key={p.key} provider={p} isSuperAdmin={isSuperAdmin} onChange={onChange} />)}
          </div>
        </>
      )}

      {tab === "routing" && <RoutingPanel />}
    </>
  );
}
